import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Select,
  Option,
  Textarea,
  Button,
  Box,
} from '@mui/joy';

interface LostReasonModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes: string) => void;
  opportunityName: string;
}

const LOST_REASONS = [
  { value: 'price', label: 'Price Too High' },
  { value: 'competitor', label: 'Lost to Competitor' },
  { value: 'no_decision', label: 'No Decision Made' },
  { value: 'no_budget', label: 'No Budget' },
  { value: 'unresponsive', label: 'Contact Unresponsive' },
  { value: 'timeline', label: 'Timeline Mismatch' },
  { value: 'poor_fit', label: 'Poor Fit' },
  { value: 'other', label: 'Other' },
];

export const LostReasonModal: React.FC<LostReasonModalProps> = ({
  open,
  onClose,
  onConfirm,
  opportunityName,
}) => {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm(reason, notes);
    // Reset form
    setReason('');
    setNotes('');
  };

  const handleClose = () => {
    // Reset form
    setReason('');
    setNotes('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        size="md"
        sx={{
          minWidth: { xs: 0, sm: 450 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: 600 },
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Mark Opportunity as Lost
        </Typography>
        
        <Typography level="body-sm" sx={{ mb: 3 }}>
          Why was the opportunity "{opportunityName}" lost?
        </Typography>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Lost Reason</FormLabel>
          <Select
            value={reason}
            onChange={(_, value) => setReason(value as string)}
            placeholder="Select a reason"
            required
          >
            {LOST_REASONS.map((r) => (
              <Option key={r.value} value={r.value}>
                {r.label}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormLabel>Additional Notes</FormLabel>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional details..."
            minRows={3}
          />
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={handleClose}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="danger"
            onClick={handleConfirm}
            disabled={!reason}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Mark as Lost
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};